import styled, {css} from 'styled-components';

import {hexToRgba} from '@edna/utils';

import {TButtonProps, TLoadingButtonProps} from '.';
import {mixins} from '../styles';
import {EButtonAppearance, EButtonSize, EShadowType, ICON_SIZE} from './types';

const getButtonShadow = (color: string, type: EShadowType) => {
  const SHADOWS: Record<EShadowType, string> = {
    [EShadowType.NORMAL]: `0 6px 2px -4px ${hexToRgba(color, 0.1)}, 2px -2px 6px -2px ${hexToRgba(
      color,
      0.1,
    )}`,
    [EShadowType.DISABLED]: `0 4px 2px -4px ${hexToRgba(color, 0.1)}, 2px -2px 4px -4px ${hexToRgba(
      color,
      0.1,
    )}`,
  };

  return SHADOWS[type];
};

const getButtonFontSize = ({size}: TButtonProps) => {
  switch (size) {
    case EButtonSize.XXS:
      return css`
        font-size: 8px;
        line-height: 8px;
        font-weight: 400;
      `;
    case EButtonSize.XS:
      return css`
        ${mixins.f1}
        line-height: 8px;
        font-weight: 500;
      `;
    case EButtonSize.S:
      return css`
        ${mixins.f2}
        line-height: 13px;
        font-weight: 500;
      `;
    case EButtonSize.M:
    case EButtonSize.L:
    default:
      return css`
        ${mixins.f3}
        line-height: 14px;
        font-weight: 600;
      `;
  }
};

const getButtonSizeStyle = ({size, theme: {dimensions}, square}: TTheme & TButtonProps) => {
  switch (size) {
    case EButtonSize.XXS:
      return css`
        height: 20px;
        padding: ${square ? '4px' : '4px 10px'};
        border-width: 1px;
        border-radius: 4px;
      `;
    case EButtonSize.XS:
      return css`
        height: ${dimensions.controlHeightXS}px;
        padding: ${square ? '6px' : '6px 10px'};
        border-width: 1.2px;
        border-radius: 6px;
      `;
    case EButtonSize.S:
      return css`
        height: ${dimensions.controlHeightS}px;
        padding: ${square ? '6px' : '6px 16px'};
        border-width: 1.6px;
        border-radius: 6px;
      `;
    case EButtonSize.L:
      return css`
        height: ${dimensions.controlHeightL}px;
        padding: ${square ? '12px' : '12px 30px'};
        border-width: 2px;
        border-radius: 12px;
      `;
    case EButtonSize.M:
    default:
      return css`
        height: ${dimensions.controlHeightM}px;
        padding: ${square ? '8px' : '8px 20px'};
        border-width: 2px;
        border-radius: 8px;
      `;
  }
};

const getButtonAppearanceStyle = ({
  theme: {palette, branding, boxShadow},
  disabled,
  appearance,
  square,
}: TTheme & TLoadingButtonProps) => {
  const shadowColor = branding.content.primary;

  if (disabled) {
    switch (appearance) {
      case EButtonAppearance.primary:
        return css`
          color: ${branding.colors.disabled};
          background-color: ${branding.backgrounds.secondary};
          border-color: ${palette.grey100};
          border-width: 1px;
          box-shadow: ${getButtonShadow(shadowColor, EShadowType.DISABLED)};
        `;
      case EButtonAppearance.secondary:
        return css`
          color: ${palette.grey300};
          background-color: ${branding.backgrounds.primary};
          border-color: ${palette.grey300};
          box-shadow: ${getButtonShadow(shadowColor, EShadowType.DISABLED)};
        `;
      case EButtonAppearance.tertiary:
      case EButtonAppearance.default:
      default:
        return css`
          color: ${branding.colors.disabled};
          background-color: ${branding.backgrounds.primary};
          border-color: ${palette.white};
          border-width: 1px;
          box-shadow: ${square ? boxShadow.XS : 'none'};
        `;
    }
  }

  switch (appearance) {
    case EButtonAppearance.primary: {
      return css`
        color: ${palette.white};
        background-color: ${branding.content.primary};
        border: none;
        box-shadow: ${getButtonShadow(shadowColor, EShadowType.NORMAL)};
        &:hover {
          color: ${branding.content.tertiary};
          background-color: ${branding.content.primary};
          border-color: ${branding.content.primary};
        }
        &:active {
          color: ${palette.white};
          background-color: ${palette.grey700};
          border-color: ${palette.grey700};
        }
      `;
    }

    case EButtonAppearance.secondary: {
      return css`
        color: ${branding.content.primary};
        background-color: ${branding.backgrounds.primary};
        border-color: ${branding.content.primary};
        box-shadow: ${getButtonShadow(shadowColor, EShadowType.NORMAL)};
        &:hover {
          color: ${palette.grey500};
          background-color: ${branding.backgrounds.primary};
          border-color: ${palette.grey500};
        }
        &:active {
          color: ${branding.content.secondary};
          background-color: ${branding.backgrounds.primary};
          border-color: ${branding.content.secondary};
        }
      `;
    }
    case EButtonAppearance.tertiary:
    case EButtonAppearance.default:
    default:
      return css`
        color: ${branding.content.primary};
        background-color: ${branding.backgrounds.primary};
        border-color: ${palette.white};
        box-shadow: ${boxShadow.XS};
        &:active {
          background-color: ${branding.backgrounds.secondary};
          border-color: ${palette.grey50};
        }
      `;
  }
};

const getButtonLayoutStyle = ({block}: TButtonProps) => {
  if (block) {
    return css`
      display: flex;
      width: 100%;
    `;
  }

  return css`
    display: inline-flex;
  `;
};

const getLoadingButtonStyle = ({loading}: TLoadingButtonProps) => {
  if (loading) {
    return css`
      cursor: progress;

      &:disabled {
        cursor: not-allowed;
      }
    `;
  }

  return '';
};

export const Button = styled.button<TButtonProps>`
  ${mixins.flexAlign('center')}
  position: relative;
  outline: none;
  border-style: solid;
  user-select: none;
  background-color: transparent;
  text-transform: none;
  text-decoration: none;
  ${getButtonFontSize}
  ${getButtonSizeStyle}
  ${getButtonAppearanceStyle}
  ${getButtonLayoutStyle}
  ${({theme, size, disabled, hasMarginLeft}) => css`
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    margin: ${hasMarginLeft ? `0 0 0 ${theme.spacing.s2}` : '0'};
    transition: all ${theme.animation.duration}ms;
    svg {
      flex-shrink: 0;
      ${size && mixins.wh(ICON_SIZE[size])}
    }
  `}
`;

export const LoadingButton = styled(Button)<TLoadingButtonProps>`
  ${getLoadingButtonStyle}
`;

export const SpinnerWrapper = styled.div`
  ${mixins.position({position: 'absolute', top: '50%', left: '50%'})}
  transform: translate(-50%, -50%);
`;

type TChildren = {
  isHidden?: boolean;
};

export const Children = styled.div<TChildren>`
  display: flex;
  align-items: center;
  max-width: 100%;
  ${({isHidden}: TChildren) => isHidden && 'visibility:hidden;'}
`;
